import {v4 as uuidv4} from 'uuid';
import type {ReactNode} from 'react';

export enum LetterCase {
  SENTENCE_CASE = 'SENTENCE_CASE',
  UPPER_CASE = 'UPPER_CASE',
  LOWER_CASE = 'LOWER_CASE',
}

export enum FuzzyMatchStrength {
  /** Accept any positive score, including weak partial-token overlap. */
  WEAK = 'weak',
  /** Require at least all input tokens to match (default). */
  NORMAL = 'normal',
  /** Require a normalized substring relationship between input and candidate. */
  STRONG = 'strong',
  /** Require an exact (normalized) match. */
  EXACT = 'exact',
}

const FUZZY_SCORE_EXACT = 1000;
const FUZZY_SCORE_CANDIDATE_CONTAINS_INPUT = 500;
const FUZZY_SCORE_INPUT_CONTAINS_CANDIDATE = 300;
const FUZZY_SCORE_ALL_TOKENS_MATCHED = 100;
const FUZZY_SCORE_PER_PARTIAL_TOKEN = 10;
const FUZZY_MIN_TOKEN_LENGTH = 2;

const FUZZY_STRENGTH_THRESHOLDS: Record<FuzzyMatchStrength, number> = {
  [FuzzyMatchStrength.WEAK]: 1,
  [FuzzyMatchStrength.NORMAL]: FUZZY_SCORE_ALL_TOKENS_MATCHED,
  [FuzzyMatchStrength.STRONG]: FUZZY_SCORE_INPUT_CONTAINS_CANDIDATE,
  [FuzzyMatchStrength.EXACT]: FUZZY_SCORE_EXACT,
};

export const isString = (value: unknown): value is string => {
  return typeof value === 'string' || value instanceof String;
};

export function toWellFormed(s: string): string {
  return s.replace(/\p{Surrogate}/gu, '\uFFFD');
}

/**
 * Replaces a placeholder string with a ReactNode, returning an array of ReactNodes.
 * Useful for interpolating React components into translated strings.
 */
export const replaceStringWithReactNode = (text: string, placeholder: string, reactNode: ReactNode): ReactNode[] => {
  return text.split(placeholder).reduce<ReactNode[]>((prev, current, i) => {
    if (!i) {
      return [current];
    }

    return prev.concat(reactNode, current);
  }, []);
};

/**
 * Check if the string already exist, returns a valid unique string if the string is already taken
 * @param {string} string the string that we want to check duplication for
 * @param {Object} stringMap map of all the strings to compare the given string
 * @returns {string}
 */
export const makeStringUnique = (string: string, stringMap: Record<string, any>): string => {
  let newString = string;
  for (let i = 0; i <= Object.keys(stringMap).length; i++) {
    if (i > 0) {
      newString = `${string} (${i.toString()})`;
    }
    if (!stringMap[newString]) {
      break;
    }
  }
  return newString;
};

export const applyLetterCase = (str: string, letterCase: LetterCase): string => {
  switch (letterCase) {
    case LetterCase.SENTENCE_CASE:
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    case LetterCase.UPPER_CASE:
      return str.toUpperCase();
    case LetterCase.LOWER_CASE:
      return str.toLowerCase();
    default:
      throw new Error(`Unhandled letterCase`);
  }
};

export const padString = (input: string, paddingString: string, totalOutputLength: number): string => {
  return (Array(totalOutputLength).join(paddingString) + input).slice(-totalOutputLength);
};

export const incrementVersionInString = (string: string, maxLength?: number): string => {
  let versionString = '';
  let newString = string;

  if (doesVersionNumberExistAtEndOfString(newString)) {
    for (let i = newString.length - 1; i >= 0; i--) {
      if (newString[i] === '(') {
        const copyNumber = parseInt(newString.substring(i + 1, newString.length - 1), 10) + 1;
        versionString = ` (${copyNumber})`;
        newString = newString.substring(0, i).trim();
        break;
      }
    }
  } else {
    versionString = ' (1)';
  }

  if (maxLength) {
    return `${newString.substring(0, maxLength - versionString.length)}${versionString}`;
  }
  return `${newString}${versionString}`;
};

export const capitalize = (string: string): string => {
  return string.substr(0, 1).toUpperCase() + string.substr(1).toLowerCase();
};

export const capitalizeFirstLetter = (string: string): string => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const lowerCaseFirstLetter = (string: string): string => {
  return string.charAt(0).toLowerCase() + string.slice(1);
};

/**
 * Capitalizes the first letter of every word.
 * Given option to lower case the remaining letters of the words.
 * @param input
 * @param shouldLowerCaseRemainingLetters
 */
export const capitalizeFirstLetterOfAllWords = (input: string, shouldLowerCaseRemainingLetters = false): string => {
  const capitalizeFunc = shouldLowerCaseRemainingLetters ? capitalize : capitalizeFirstLetter;

  return input
    .split(' ')
    .map((word) => {
      return capitalizeFunc(word);
    })
    .join(' ');
};

/**
 * Returns a rfc4122 version 4 compliant UUID.
 * TODO: Maybe we should explore the built in crypto.randomUUID() function instead of the getUniqueString() function. link: https://developer.mozilla.org/en-US/docs/Web/API/Crypto/randomUUID
 * @returns {String}
 */
export const getUniqueString = (): string => {
  return uuidv4();
};

export const getUniqueId = (length = 16): string => {
  return crypto.getRandomValues(new Uint8Array(length)).reduce((acc, byte) => acc + byte.toString(16).padStart(2, '0'), '');
};

export const escapeHTML = (stringToEscape: string): string => {
  if (stringToEscape === null || stringToEscape === '') {
    return '';
  }

  return stringToEscape.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\"/g, '&quot;').replace(/\'/g, '&#39;').replace(/\//g, '&#x2F;');
};

export const isValidEmail = (email: string): boolean => {
  return /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?$/i.test(
    email
  );
};

export const hexToAscii = (hex: string): string => {
  let str = '';
  for (let n = 0; n < hex.length; n += 2) {
    str += String.fromCharCode(parseInt(hex.substring(n, n + 2), 16));
  }
  return str;
};

/**
 * checks if the string has alphanumerics only (A-Z and 0-9)
 */
export const hasAlphaNumericsOnly = (string: string): boolean => {
  const regexExpression = /[^A-Z0-9]/gi;
  const matches = string.match(regexExpression);
  return !(matches && matches.length > 0);
};

export const isNumericString = (string: string): boolean => {
  return Number.isInteger(Number(string));
};

const doesVersionNumberExistAtEndOfString = (string: string): boolean => {
  // the regex expression below checks for (i) at the end of the string. {i} has to be an integer
  const regex = /\([0-9]+\)$/;
  return regex.test(string);
};

export const getHeightFromPixelHeight = (height: string): number => {
  return Number(height.replace('px', ''));
};

/**
 * Returns the first family from a CSS font-family list, unquoted (e.g. `'"Open Sans", Arial'` -> `Open Sans`).
 * Lives here (not font.util) so headless Node consumers don't pull in font.util's browser-only dependency graph.
 */
export const parsePrimaryFontFamily = (fontFamily: string): string => {
  const primary = fontFamily.split(',')[0].trim();
  return primary.replace(/^['"]+|['"]+$/g, '');
};

export const isJson = (data: string): boolean => {
  try {
    JSON.parse(data);
    return true;
  } catch {
    return false;
  }
};

export const getCamelCasedString = (input: string, delimiter: string = '_'): string => {
  if (!input) {
    return '';
  }

  const words = input.split(delimiter);
  if (words.length === 1) {
    return words[0].toLowerCase();
  }

  return (
    words[0].toLowerCase() +
    words
      .slice(1)
      .map((word: string) => {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join('')
  );
};

export const getSeparatedStringFromCamelCase = (input: string, newCase: LetterCase = LetterCase.SENTENCE_CASE, delimiter: string = ' '): string => {
  if (!input) {
    return input;
  }

  let result = input.replace(/([A-Z])/g, `${delimiter}$1`);
  switch (newCase) {
    case LetterCase.SENTENCE_CASE:
      result = result.toLowerCase();
      return result.charAt(0).toUpperCase() + result.slice(1);
    case LetterCase.LOWER_CASE:
      return result.toLowerCase();
    case LetterCase.UPPER_CASE:
      return result.toUpperCase();
    default:
      return result;
  }
};

export const removePunctuationFromWord = (text: string): string => {
  return text.replace(/[^\p{L}\p{N}\s]/gu, '').replace(/\s+/g, ' ');
};

export const xssCleanHtml = (content: string): string => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = content;
  const harmfulAttributes = [
    'onerror',
    'onload',
    'onclick',
    'onmouseover',
    'onfocus',
    'onblur',
    'onmousedown',
    'ontoggle',
    'onmouseenter',
    'onmouseleave',
    'onkeydown',
    'onkeyup',
    'onkeypress',
    'onwheel',
    'onsubmit',
    'onreset',
    'onchange',
    'oninput',
  ];
  const elements = tempDiv.getElementsByTagName('*');

  for (let i = 0; elements.length > i; i++) {
    harmfulAttributes.forEach((attr) => {
      elements[i].removeAttribute(attr);
    });
  }

  return Array.from(tempDiv.childNodes)
    .map((node) => (node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement).outerHTML : node.textContent))
    .join('');
};

/**
 * Generic function to find an Enum member by its value or key.
 * @param enumObj - The Enum object (e.g., ErrorCodes)
 * @param value - The string to search for
 * @param checkKeys - If true, checks against the keys; otherwise, checks the values.
 */
export const getEnumMember = <T extends object>(enumObj: T, value: string, checkKeys = false): T[keyof T] | undefined => {
  if (!value) return undefined;

  const foundEntry = Object.entries(enumObj).find(([key, val]) => {
    return checkKeys ? key.toLowerCase() === value.toLowerCase() : String(val).toLowerCase() === value.toLowerCase();
  });
  return foundEntry ? (foundEntry[1] as T[keyof T]) : undefined;
};

/**
 * Returns the candidate that best fuzzy-matches the input, or null when no candidate clears the strength threshold.
 * Scoring tiers, highest first: exact match, candidate contains input, input contains candidate, token-prefix matches.
 * Matching is diacritic-insensitive and ignores single-character tokens to avoid spurious letter-level matches.
 */
export const findBestFuzzyMatch = (input: string, candidates: readonly string[], strength: FuzzyMatchStrength = FuzzyMatchStrength.NORMAL): string | null => {
  if (!input || input.trim() === '' || candidates.length === 0) {
    return null;
  }

  const minScore = FUZZY_STRENGTH_THRESHOLDS[strength];
  let bestCandidate: string | null = null;
  let bestScore = 0;

  for (const candidate of candidates) {
    const score = scoreFuzzyMatch(input, candidate);
    if (score >= minScore && score > bestScore) {
      bestScore = score;
      bestCandidate = candidate;
    }
  }

  return bestCandidate;
};

const normalizeForFuzzyMatch = (value: string): string => {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
};

const tokenizeForFuzzyMatch = (value: string): string[] => {
  return normalizeForFuzzyMatch(value)
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length >= FUZZY_MIN_TOKEN_LENGTH);
};

const isTokenMatch = (inputToken: string, candidateTokens: string[]): boolean => {
  return candidateTokens.some((candidateToken) => candidateToken.startsWith(inputToken) || inputToken.startsWith(candidateToken));
};

const scoreFuzzyMatch = (input: string, candidate: string): number => {
  const inputNormalized = normalizeForFuzzyMatch(input);
  const candidateNormalized = normalizeForFuzzyMatch(candidate);

  if (inputNormalized === '' || candidateNormalized === '') {
    return 0;
  }

  if (inputNormalized === candidateNormalized) {
    return FUZZY_SCORE_EXACT;
  }

  if (candidateNormalized.includes(inputNormalized)) {
    return FUZZY_SCORE_CANDIDATE_CONTAINS_INPUT + (inputNormalized.length / candidateNormalized.length) * 100;
  }

  if (inputNormalized.includes(candidateNormalized)) {
    return FUZZY_SCORE_INPUT_CONTAINS_CANDIDATE + (candidateNormalized.length / inputNormalized.length) * 100;
  }

  const inputTokens = tokenizeForFuzzyMatch(input);
  const candidateTokens = tokenizeForFuzzyMatch(candidate);

  if (inputTokens.length === 0 || candidateTokens.length === 0) {
    return 0;
  }

  const matchedTokens = inputTokens.filter((token) => isTokenMatch(token, candidateTokens));

  if (matchedTokens.length === 0) {
    return 0;
  }

  const matchedCharCount = matchedTokens.reduce((sum, token) => sum + token.length, 0);

  if (matchedTokens.length === inputTokens.length) {
    return FUZZY_SCORE_ALL_TOKENS_MATCHED + (matchedCharCount / candidateNormalized.length) * 50;
  }

  return matchedTokens.length * FUZZY_SCORE_PER_PARTIAL_TOKEN;
};

/**
 * @param value
 * @param allowed
 * @param fallback
 */
export const stringToEnum = <T extends string>(value: string | undefined, allowed: readonly T[], fallback: T): T => {
  return value !== undefined && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
};